import React from 'react';
import { motion } from 'framer-motion';
import { User, Sparkles, Image as ImageIcon, File as FileIcon, Video, Download } from 'lucide-react';

const MessageBubble = ({ message, index }) => {
  const isUser = message.role === 'user';
  const timestamp = new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const hasAttachments = message.attachments && message.attachments.length > 0;

  const getFileIcon = (contentType) => {
    if (contentType.startsWith('image/')) return <ImageIcon size={14} />;
    if (contentType.startsWith('video/')) return <Video size={14} />;
    return <FileIcon size={14} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const downloadFile = (attachment) => {
    const link = document.createElement('a');
    link.href = `data:${attachment.content_type};base64,${attachment.data}`;
    link.download = attachment.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start`}
      data-testid={`chat-message-${isUser ? 'user' : 'ai'}-${message.message_id}`}
    >
      {/* Avatar icon */}
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser ? 'bg-sky-500/20 text-sky-300' : 'bg-cyan-500/20 text-cyan-300'
        }`}
      >
        {isUser ? <User size={18} /> : <Sparkles size={18} />}
      </div>

      {/* Message bubble */}
      <div
        className={`max-w-[75%] md:max-w-[60%] rounded-2xl p-4 backdrop-blur-md ${
          isUser
            ? 'bg-sky-500/15 text-sky-100 border border-sky-400/20 rounded-br-md'
            : 'bg-cyan-500/10 text-cyan-100 border border-cyan-400/20 rounded-bl-md'
        }`}
        style={{ boxShadow: 'var(--shadow-soft)' }}
      >
        {/* Attachments */}
        {hasAttachments && (
          <div className="mb-3 space-y-2">
            {message.attachments.map((attachment, idx) => (
              <div key={idx} className="rounded-lg overflow-hidden">
                {attachment.content_type.startsWith('image/') ? (
                  <img
                    src={`data:${attachment.content_type};base64,${attachment.data}`}
                    alt={attachment.filename}
                    className="max-w-full h-auto rounded-lg"
                    style={{ maxHeight: '300px', objectFit: 'contain' }}
                  />
                ) : attachment.content_type.startsWith('video/') ? (
                  <video
                    controls
                    className="max-w-full h-auto rounded-lg"
                    style={{ maxHeight: '300px' }}
                  >
                    <source src={`data:${attachment.content_type};base64,${attachment.data}`} type={attachment.content_type} />
                    Votre navigateur ne supporte pas la lecture vidéo.
                  </video>
                ) : (
                  <div
                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ background: 'rgba(0,0,0,0.2)' }}
                    onClick={() => downloadFile(attachment)}
                  >
                    <div className="flex-shrink-0">
                      {getFileIcon(attachment.content_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{attachment.filename}</p>
                      <p className="text-xs opacity-70">{formatFileSize(attachment.size)}</p>
                    </div>
                    <Download size={16} className="flex-shrink-0" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Message text */}
        {message.content && (
          <p className="font-ui text-sm md:text-base whitespace-pre-wrap leading-relaxed">
            {message.content}
          </p>
        )}

        {/* Meta info */}
        <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
          <span>{timestamp}</span>
          {message.message_type === 'voice' && (
            <span className="flex items-center gap-1">
              <span>🎤</span>
              <span>Voice</span>
            </span>
          )}
          {message.message_type === 'image' && (
            <span className="flex items-center gap-1">
              <ImageIcon size={12} />
              <span>Image</span>
            </span>
          )}
          {message.message_type === 'file' && (
            <span className="flex items-center gap-1">
              <FileIcon size={12} />
              <span>Fichier</span>
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MessageBubble;
